<template>
	<div class="supplier-performance-map">
		<v-row align="center" justify="center" class="pt-2">
			<v-col cols="auto">
				<h2 class="map-title mb-0">Supplier Performance Map of</h2>
			</v-col>
			<v-col cols="auto">
				<v-select
					v-model="selectedType"
					:items="kpiTypes"
					outlined
					dense
					hide-details
					style="width: 170px;"
					@change="loadMap"
				></v-select>
			</v-col>
		</v-row>

		<p class="map-description mt-6 mb-2">
			KPI scores are measured on a scale from 1 to 5, where 5 represents the
			highest performance. This map shows the average {{ selectedType.toLowerCase() }}
			score for the countries of <strong>your connections</strong>.
			When multiple suppliers share a country, each supplier gets its own
			vertical slice (lowest score on the left, highest on the right).
		</p>
		<p class="map-source mb-2">
			Source: Supplier KPI evaluations entered in Orixe.
		</p>

		<div class="map-legend mb-4">
			<p class="map-legend-text mb-0">
				<span class="legend-item">
					<span class="legend-dot legend-dot--green"></span>
					Green &gt; {{ KPI_TIER_GREEN_MIN }}
				</span>
				<span class="legend-separator">·</span>
				<span class="legend-item">
					<span class="legend-dot legend-dot--yellow"></span>
					Yellow {{ KPI_TIER_RED_MAX }}-{{ KPI_TIER_GREEN_MIN }}
				</span>
				<span class="legend-separator">·</span>
				<span class="legend-item">
					<span class="legend-dot legend-dot--red"></span>
					Red &lt; {{ KPI_TIER_RED_MAX }}
				</span>
			</p>
		</div>

		<v-card flat outlined class="map-embed-card">
			<supplier-performance-world-map :countries="mapCountries" />
		</v-card>

		<div v-if="mapSuppliers.length" class="map-supplier-summary mt-4">
			<p class="map-supplier-summary-title mb-2">
				{{ mapSuppliers.length }} suppliers · {{ mapCountries.length }}
				{{ mapCountries.length === 1 ? "country" : "countries" }}
			</p>
			<ul class="map-supplier-summary-list mb-0 pl-0">
				<li
					v-for="supplier in mapSuppliers"
					:key="supplier.clientID || `${supplier.name}-${supplier.country}`"
					class="map-supplier-summary-item"
				>
					<span
						class="legend-dot"
						:style="{ background: supplier.fill }"
					></span>
					<router-link
						v-if="supplier.clientID"
						:to="{
							name: 'Single Client View',
							params: { id: supplier.clientID },
							query: { focus: 'performance' },
						}"
						class="map-supplier-summary-link"
					>
						<strong>{{ supplier.name }}</strong>
					</router-link>
					<strong v-else>{{ supplier.name }}</strong>
					<span class="map-supplier-summary-meta">
						{{ supplier.country }} · {{ selectedType }} {{ supplier.score }}
						<span v-if="!supplier.mapId"> · not shown on map</span>
					</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
import SupplierAPI from "../api/csr/supplier"
import ConnectionAPI from "../api/connections/connection"
import SupplierPerformanceWorldMap from "../components/SupplierPerformanceWorldMap.vue"
import LoadingState from "../consts/LoadingState"
import { buildSupplierPerformanceMapCountries } from "../utils/connectionMapChart"
import {
	calculateKpiMean,
	formatKpiScore,
	KPI_TIER_GREEN_MIN,
	KPI_TIER_RED_MAX,
} from "../utils/supplierKpiOverall"

const KPI_FIELDS = {
	Quality: "qualityKpi",
	Delivery: "deliveryKpi",
	Support: "supportKpi",
	Overall: "overall",
}

export default {
	name: "SupplierPerformanceConnectionMap",
	components: {
		SupplierPerformanceWorldMap,
	},
	data() {
		return {
			selectedType: "Quality",
			kpiTypes: ["Quality", "Delivery", "Support", "Overall"],
			mapCountries: [],
			mapSuppliers: [],
			KPI_TIER_GREEN_MIN,
			KPI_TIER_RED_MAX,
		}
	},
	mounted() {
		this.loadMap()
	},
	methods: {
		getKpiScore(performance, type) {
			if (!performance) {
				return null
			}
			if (type === "Overall") {
				return calculateKpiMean(
					performance.qualityKpi,
					performance.deliveryKpi,
					performance.supportKpi,
				)
			}
			const field = KPI_FIELDS[type]
			return performance[field] ?? null
		},
		formatBreakdownLabel(score) {
			if (score === null || score === undefined) {
				return "No KPI"
			}
			return formatKpiScore(score)
		},
		async loadMap() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true },
			)

			try {
				const [connections, overview, supplierDetails] = await Promise.all([
					ConnectionAPI.getConnections(),
					SupplierAPI.getSupplierPerformanceOverview(),
					SupplierAPI.getSuppliers(),
				])

				const overviewByClientId = new Map(
					overview.map((row) => [Number(row.clientID), row]),
				)
				const cooByClientId = new Map(
					supplierDetails.map((row) => [Number(row.clientID), row.COO]),
				)

				let entries = []
				if (connections.length > 0) {
					entries = connections.map((connection) => ({
						clientID: connection.clientID,
						name: connection.name || connection.toName || connection.fromName,
					}))
				} else if (overview.length > 0) {
					entries = overview.map((row) => ({
						clientID: row.clientID,
						name: row.name,
					}))
				} else {
					entries = supplierDetails.map((row) => ({
						clientID: row.clientID,
						name: row.name,
					}))
				}

				const seenClientIds = new Set()
				entries = entries.filter((entry) => {
					const clientID = Number(entry.clientID)
					if (!clientID || seenClientIds.has(clientID)) {
						return false
					}
					seenClientIds.add(clientID)
					return true
				})

				const countryArray = []
				const companyArray = []
				const rawKpiScores = []
				const breakdownLabels = []
				const clientIds = []

				for (const entry of entries) {
					const clientID = Number(entry.clientID)
					const performance = overviewByClientId.get(clientID)
					const rawScore = this.getKpiScore(performance, this.selectedType)
					const breakdownLabel = this.formatBreakdownLabel(rawScore)
					const country = cooByClientId.get(clientID)

					clientIds.push(clientID)
					countryArray.push(country || "Undefined")
					companyArray.push(entry.name || "—")
					rawKpiScores.push(rawScore)
					breakdownLabels.push(breakdownLabel)
				}

				const mapData = buildSupplierPerformanceMapCountries(
					countryArray,
					companyArray,
					rawKpiScores,
					breakdownLabels,
					clientIds,
				)

				this.mapCountries = mapData.countries
				this.mapSuppliers = mapData.suppliers
			} catch (error) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					error,
					{ root: true },
				)
			} finally {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_END_LOADING,
					null,
					{ root: true },
				)
			}
		},
	},
}
</script>

<style scoped>
.map-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1a1a1a;
}

.map-embed-card {
	border-radius: 8px;
	overflow: visible;
	padding: 16px;
}

.map-description {
	color: #424242;
	line-height: 1.6;
}

.map-source {
	font-weight: bold;
	color: #424242;
}

.map-legend {
	text-align: center;
}

.map-legend-text {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	color: #616161;
	font-size: 0.875rem;
	line-height: 1.6;
}

.legend-item {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
}

.legend-separator {
	margin: 0 8px;
	color: #bdbdbd;
}

.legend-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	margin-right: 6px;
	flex-shrink: 0;
}

.legend-dot--green {
	background: #2e7d32;
}

.legend-dot--yellow {
	background: #f9a825;
}

.legend-dot--red {
	background: #c62828;
}

.map-supplier-summary {
	padding: 0 4px;
}

.map-supplier-summary-title {
	font-size: 0.875rem;
	font-weight: 600;
	color: #424242;
}

.map-supplier-summary-list {
	list-style: none;
}

.map-supplier-summary-item {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	font-size: 0.875rem;
	color: #424242;
	margin-bottom: 8px;
}

.map-supplier-summary-meta {
	color: #757575;
}

.map-supplier-summary-link {
	color: #1e7c83;
	text-decoration: none;
}

.map-supplier-summary-link:hover {
	text-decoration: underline;
}
</style>
